
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function KenilworthPage() {
  useEffect(() => {
    try {
      // Update page title and meta tags
      document.title =
        'Kenilworth Painters Cape Town | Residential Property Painting Services | Quality Home Painting';

      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          'Professional painting services for Kenilworth residential properties. Quality interior & exterior painting for family homes with expert craftsmanship and reliable service.'
        );
      }

      // Update meta keywords
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute(
          'content',
          'Kenilworth painters, residential painting, family home painting, Kenilworth homes, Cape Town house painters'
        );
      }

      // Update canonical URL
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute(
          'href',
          `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/kenilworth`
        );
      }

      // Add Schema.org JSON‑LD
      const schemaData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Kenilworth Painters - Residential Property Painting Services',
        description:
          'Professional painting services for Kenilworth residential properties with quality craftsmanship and reliable service.',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/kenilworth`,
        mainEntity: {
          '@type': 'LocalBusiness',
          name: 'Cape Town Painters - Kenilworth',
          description:
            "Quality residential painting services for Kenilworth's family homes and properties.",
          areaServed: 'Kenilworth, Cape Town',
          serviceType: 'Residential Property Painting Services',
        },
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemaData, null, 2);
      document.head.appendChild(script);
    } catch (err) {
      // Fail‑soft – log the error but avoid breaking the UI
      console.error('SEO metadata injection failed:', err);
    }

    // Cleanup the injected JSON‑LD script on unmount
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Kenilworth')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Family Home Painting',
      description:
        'Complete interior and exterior painting services for Kenilworth family homes, focusing on durability, safety, and beautiful finishes.',
      price: 'From R12,000',
      features: [
        'Family-safe paints',
        'Durable finishes',
        'Color consultation',
        'Interior & exterior',
        'Quality materials',
      ],
    },
    {
      icon: 'ri-building-line',
      title: 'Townhouse Painting',
      description:
        'Specialized painting services for Kenilworth townhouses and cluster homes, including coordinated exterior schemes and quality interior work.',
      price: 'From R10,000',
      features: [
        'Townhouse expertise',
        'Coordinated schemes',
        'Efficient scheduling',
        'Minimal disruption',
        'Professional finish',
      ],
    },
    {
      icon: 'ri-palette-line',
      title: 'Interior Renovations',
      description:
        'Complete interior painting and renovation services for Kenilworth homes, transforming living spaces with modern colors and finishes.',
      price: 'From R8,000',
      features: [
        'Modern color schemes',
        'Room transformations',
        'Feature walls',
        'Ceiling work',
        'Trim & details',
      ],
    },
  ];

  const features = [
    {
      title: 'Residential Specialists',
      description:
        'Extensive experience in residential painting with deep understanding of family home requirements, safety considerations, and quality expectations.',
    },
    {
      title: 'Family-Friendly Service',
      description:
        'Safe, reliable service designed for family homes with minimal disruption to daily routines and use of family-safe, low-VOC paint products.',
    },
    {
      title: 'Quality Craftsmanship',
      description:
        "Professional painting techniques and attention to detail ensuring beautiful, long-lasting results that enhance your Kenilworth home's value.",
    },
    {
      title: 'Local Area Knowledge',
      description:
        "Deep understanding of Kenilworth's residential architecture and community standards, ensuring appropriate finishes and color choices.",
    },
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Home Consultation',
      description:
        "Comprehensive assessment of your Kenilworth home with color consultation and planning tailored to your family's needs and lifestyle.",
    },
    {
      step: 2,
      title: 'Careful Preparation',
      description:
        'Thorough surface preparation with protection of furniture and belongings, ensuring a clean and safe work environment.',
    },
    {
      step: 3,
      title: 'Professional Application',
      description:
        'Expert painting using quality materials and proven techniques, with attention to detail and minimal disruption to your family routine.',
    },
    {
      step: 4,
      title: 'Quality Completion',
      description:
        'Final inspection and cleanup ensuring your Kenilworth home is left clean, beautiful, and ready for your family to enjoy.',
    },
  ];

  const testimonials = [
    {
      name: 'David Wilson',
      property: 'Family Home',
      rating: 5,
      text: 'Excellent work on our Kenilworth home. The team was professional, reliable, and worked around our family schedule. The results exceeded our expectations and the house looks fantastic.',
      image:
        'https://public.readdy.ai/ai/img_res/c6c627c8d53f5c7ea62a1e7460fd83e5.jpg',
    },
    {
      name: 'Jennifer Adams',
      property: 'Townhouse',
      rating: 5,
      text: 'Outstanding service for our townhouse renovation. The color consultation was helpful and the painting quality is excellent. Highly recommend for any Kenilworth residents.',
      image:
        'https://readdy.ai/api/search-image?query=Woman%20homeowner%20at%20Kenilworth%20townhouse%2C%20satisfied%20client%20portrait%2C%20residential%20property%20setting&width=80&height=80&seq=kenilworth2&orientation=squarish',
    },
  ];

  const projects = [
    {
      title: 'Victorian Family Home',
      description:
        'Complete exterior and interior renovation of a classic Kenilworth Victorian home, preserving character features while updating with modern colors.',
      image:
        'https://readdy.ai/api/search-image?query=Victorian%20family%20home%20in%20Kenilworth%20with%20freshly%20painted%20exterior%2C%20residential%20street%20setting%2C%20professional%20painting%20work&width=400&height=300&seq=kenilworth3&orientation=landscape',
    },
    {
      title: 'Modern Townhouse Complex',
      description:
        'Coordinated painting project for a Kenilworth townhouse development, featuring contemporary color schemes and high-quality finishes.',
      image:
        'https://readdy.ai/api/search-image?query=Modern%20townhouse%20complex%20in%20Kenilworth%20with%20newly%20painted%20exteriors%2C%20residential%20development%2C%20quality%20finish%20work&width=400&height=300&seq=kenilworth4&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20residential%20property%20in%20Kenilworth%2C%20family%20home%20setting%2C%20painting%20equipment%2C%20suburban%20environment%2C%20sunny%20day&width=1920&height=1080&seq=kenilworth_hero&orientation=landscape')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Kenilworth
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Quality residential painting services for Kenilworth family homes. Professional craftsmanship and reliable service for your home with over 20 years of experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Kenilworth Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Quality Painting Services for Kenilworth
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Kenilworth is a well-established residential suburb known for its family-friendly atmosphere, quality homes, and strong community spirit. This popular area features a mix of architectural styles from Victorian homes to modern developments, all requiring specialized painting expertise.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our experienced team understands the unique requirements of Kenilworth's residential properties, from heritage homes that need careful restoration to modern family houses requiring durable, family-safe finishes. We pride ourselves on delivering quality results while respecting the community's residential character.
              </p>
              <p className="text-lg text-gray-600">
                Whether you're updating a family home, renovating a townhouse, or maintaining your property&#39;s value, our Kenilworth painting specialists provide reliable, professional service that enhances your home&#39;s beauty and protects your investment.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Kenilworth%20residential%20suburb%20with%20family%20homes%2C%20tree-lined%20streets%2C%20established%20neighborhood%2C%20quality%20residential%20properties&width=600&height=400&seq=kenilworth5&orientation=landscape"
                alt="Kenilworth Residential Area"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Kenilworth Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional residential painting solutions designed for Kenilworth&#39;s family homes and properties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#ffff00' }}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Us for Kenilworth?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized expertise in residential painting with family-friendly service and quality craftsmanship.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Kenilworth Painting Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A family-friendly approach designed for Kenilworth&#39;s residential properties and community standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div
                  className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold"
                  style={{ backgroundColor: '#ffff00' }}
                >
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Recent Kenilworth Projects</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our recent residential painting projects across Kenilworth&#39;s diverse property types.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img src={project.image} alt={project.title} className="w-full h-64 object-cover object-top" />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Client Reviews</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Testimonials from satisfied Kenilworth homeowners.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover mr-4" />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">Ready for Quality Painting in Kenilworth?</h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's residential painting specialists. Experience reliable service and quality craftsmanship for your Kenilworth home.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
            <a
              href="mailto:Get a quote"
              className="bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              Email Us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
